/**
 * Where the shell gets its `ApiClient` from.
 *
 * The client is handed in through context rather than constructed here so that
 * every test in this repository drives the real `ApiClient` -- real request
 * construction, real status branching, real Zod parsing -- against MSW, instead
 * of stubbing the module and proving nothing about any of it.
 *
 * `src/lib/` still imports no React and knows nothing about this file; the
 * dependency runs one way.
 */

import { createContext, useContext } from 'react';

import type { ApiClient } from '../../lib/api';

export const ApiClientContext = createContext<ApiClient | null>(null);

/**
 * Throws rather than returning null when the provider is missing. A hook that
 * returned null here would push a `client?.` into every caller and turn a
 * wiring mistake into a page that silently renders nothing.
 */
export function useApiClient(): ApiClient {
  const client = useContext(ApiClientContext);
  if (client === null) {
    throw new Error('no ApiClient in context: wrap the tree in <AppProviders>');
  }
  return client;
}
